import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Package } from 'lucide-react';
import * as Icons from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { Product } from '../types';
import PriceWithVatLabel from './PriceWithVatLabel';
import ProductNewRibbon from './ProductNewRibbon';
import { isProductNew } from '../utils/productNews';
import { trackBookingCtaClick, trackProductLinkClick, trackHomePopularPackageClick } from '../utils/siteAnalytics';
import { appendIntentToPath, readHomeBookingIntent } from '../utils/homeBookingIntent';

type ProductCarouselProps = {
  products: Product[];
  title: string;
  subtitle?: string;
  ctaLink?: string;
  ctaLabel?: string;
  loading?: boolean;
  error?: string | null;
  sectionClassName?: string;
  /** Visa alla produkter i rad på desktop (t.ex. 4 bröllopspaket). */
  showAllInView?: boolean;
  /** Homepage Populärt: Se paket / Boka paket. */
  cardCtaMode?: 'default' | 'homePopular';
};

const ProductCarousel: React.FC<ProductCarouselProps> = ({
  products,
  title,
  subtitle,
  ctaLink = '/produkter',
  ctaLabel = 'Se alla produkter',
  loading = false,
  error = null,
  sectionClassName = 'py-16 md:py-20 border-t border-slate-800',
  showAllInView = false,
  cardCtaMode = 'default',
}) => {
  const [categoryIcons, setCategoryIcons] = useState<Record<string, string>>({});
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const activeIndexRef = useRef(0);
  const programmaticScrollRef = useRef(false);
  const programmaticScrollTimerRef = useRef<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [canScroll, setCanScroll] = useState(false);

  const scrollToIndex = (idx: number) => {
    const el = scrollerRef.current;
    const card = cardRefs.current[idx];
    if (!el || !card) return;

    const clamped = Math.max(0, Math.min(products.length - 1, idx));
    activeIndexRef.current = clamped;
    setActiveIndex(clamped);

    programmaticScrollRef.current = true;
    if (programmaticScrollTimerRef.current) {
      window.clearTimeout(programmaticScrollTimerRef.current);
    }

    el.scrollTo({
      left: Math.max(0, card.offsetLeft - 16),
      behavior: 'smooth',
    });

    programmaticScrollTimerRef.current = window.setTimeout(() => {
      programmaticScrollRef.current = false;
      programmaticScrollTimerRef.current = null;
    }, 450);
  };

  const goToPrevious = () => {
    scrollToIndex(activeIndexRef.current - 1);
  };

  const goToNext = () => {
    scrollToIndex(activeIndexRef.current + 1);
  };

  const fitAllInView = showAllInView && products.length > 0 && products.length <= 4;
  const fitAllGridClass = fitAllInView
    ? products.length === 1
      ? 'lg:grid lg:grid-cols-1 lg:max-w-sm lg:mx-auto'
      : products.length === 2
        ? 'lg:grid lg:grid-cols-2 lg:max-w-3xl lg:mx-auto'
        : products.length === 3
          ? 'lg:grid lg:grid-cols-3 lg:max-w-5xl lg:mx-auto'
          : 'lg:grid lg:grid-cols-4'
    : '';
  const showArrows = canScroll && products.length > 1 && !fitAllInView;
  const atStart = activeIndex <= 0;
  const atEnd = activeIndex >= products.length - 1;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('product_categories')
          .select('name, icon')
          .order('sort_order', { ascending: true });

        if (fetchError) throw fetchError;

        const iconMap: Record<string, string> = {};
        data?.forEach((cat) => {
          iconMap[cat.name] = cat.icon;
        });
        setCategoryIcons(iconMap);
      } catch (err) {
        console.error('Error fetching product categories:', err);
      }
    };

    void fetchCategories();
  }, []);

  useEffect(() => {
    cardRefs.current = [];
    activeIndexRef.current = 0;
    setActiveIndex(0);
  }, [products]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || products.length === 0) {
      setCanScroll(false);
      return;
    }

    const updateCanScroll = () => {
      window.requestAnimationFrame(() => {
        setCanScroll(el.scrollWidth > el.clientWidth + 2);
      });
    };

    updateCanScroll();

    const resizeObserver = new ResizeObserver(updateCanScroll);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [products, fitAllInView]);

  useEffect(() => {
    if (!scrollerRef.current || products.length === 0) return;

    const root = scrollerRef.current;

    const syncActiveIndexFromScroll = () => {
      if (programmaticScrollRef.current) return;

      const cards = cardRefs.current.filter(Boolean) as HTMLDivElement[];
      if (cards.length === 0) return;

      const scrollLeft = root.scrollLeft;
      let closestIdx = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((cardEl, idx) => {
        const distance = Math.abs(cardEl.offsetLeft - 16 - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIdx = idx;
        }
      });

      if (closestIdx !== activeIndexRef.current) {
        activeIndexRef.current = closestIdx;
        setActiveIndex(closestIdx);
      }
    };

    root.addEventListener('scroll', syncActiveIndexFromScroll, { passive: true });

    return () => {
      root.removeEventListener('scroll', syncActiveIndexFromScroll);
    };
  }, [products]);

  useEffect(() => {
    return () => {
      if (programmaticScrollTimerRef.current) {
        window.clearTimeout(programmaticScrollTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (fitAllInView) return;
    if (products.length <= 1) return;
    if (isHovered) return;
    if (loading) return;

    const id = window.setInterval(() => {
      const next = (activeIndexRef.current + 1) % products.length;
      scrollToIndex(next);
    }, 6500);

    return () => window.clearInterval(id);
  }, [products.length, isHovered, loading, fitAllInView]);

  const cardWidthClass = fitAllInView
    ? 'snap-start min-w-[82%] sm:min-w-[calc(50%-0.5rem)] lg:min-w-0'
    : 'snap-start min-w-[86%] sm:min-w-[70%] md:min-w-[520px] lg:min-w-[560px]';

  const cardPaddingClass = fitAllInView ? 'p-4 sm:p-5' : 'p-7';
  const titleClass = fitAllInView ? 'text-lg sm:text-xl font-bold text-white mb-2' : 'text-2xl font-bold text-white mb-3';

  const getCategoryIcon = (category: string) => {
    const iconName = categoryIcons[category];
    if (!iconName) return Package;

    const formattedIconName = iconName
      .split('-')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');

    const IconComponent = (Icons as Record<string, React.ComponentType<{ size?: number; className?: string }>>)[formattedIconName];
    return IconComponent || Package;
  };

  if (loading) {
    return (
      <section className={`${sectionClassName} bg-slate-800/50`}>
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto" />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={`${sectionClassName} bg-slate-800/50`}>
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="bg-red-900/30 border border-red-500/50 text-red-200 px-4 py-3 rounded mb-4">
            {error}
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <section className={sectionClassName}>
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">{title}</h2>
          {subtitle ? (
            <p className="text-slate-400 max-w-2xl mx-auto">{subtitle}</p>
          ) : null}
        </div>

        <div className="relative mb-10">
          {showArrows && (
            <>
              <button
                type="button"
                onClick={goToPrevious}
                disabled={atStart}
                className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-10 h-10 bg-slate-900/40 border border-slate-800 text-white hover:bg-slate-900/60 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                aria-label="Föregående produkt"
              >
                <ArrowRight className="rotate-180" size={18} />
              </button>

              <button
                type="button"
                onClick={goToNext}
                disabled={atEnd}
                className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-10 h-10 bg-slate-900/40 border border-slate-800 text-white hover:bg-slate-900/60 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                aria-label="Nästa produkt"
              >
                <ArrowRight size={18} />
              </button>
            </>
          )}

          <div
            ref={scrollerRef}
            className={`flex gap-4 overflow-x-auto scroll-smooth pb-4 md:pb-0 ${
              fitAllInView
                ? `${fitAllGridClass} lg:overflow-x-visible lg:snap-none lg:gap-6`
                : 'snap-x snap-mandatory'
            }`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {products.map((product, index) => {
              const productPath = `/produkter/${product.slug || product.id}`;
              const bookingPath = `/booking/${product.id}`;
              const homeCtaLabel: 'Se paket' | 'Boka paket' = product.priceOnRequest
                ? 'Se paket'
                : 'Boka paket';
              const defaultCtaLabel = product.priceOnRequest ? 'Till produkt' : 'Boka nu';
              const ctaText = cardCtaMode === 'homePopular' ? homeCtaLabel : defaultCtaLabel;
              const primaryHref =
                cardCtaMode === 'homePopular'
                  ? product.priceOnRequest
                    ? appendIntentToPath(productPath, readHomeBookingIntent())
                    : bookingPath
                  : product.priceOnRequest
                    ? productPath
                    : bookingPath;

              return (
              <div
                key={product.id}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                data-index={index}
                className={cardWidthClass}
              >
                <Link
                  to={
                    cardCtaMode === 'homePopular'
                      ? appendIntentToPath(productPath, readHomeBookingIntent())
                      : productPath
                  }
                  onClick={() => {
                    trackProductLinkClick(product, title);
                    if (cardCtaMode === 'homePopular') {
                      trackHomePopularPackageClick(product.id, 'Se paket');
                    }
                  }}
                  className={`group bg-slate-900/35 border border-slate-800 overflow-visible flex flex-col h-full transition-colors
                    ${!fitAllInView && index === activeIndex ? 'border-purple-500/40' : 'hover:border-slate-700'}`}
                >
                  <div className="relative bg-slate-950/10 border-b border-slate-800 overflow-visible">
                    <div
                      className={`relative overflow-hidden ${
                        fitAllInView ? 'aspect-[2/1] max-h-44 sm:max-h-48' : 'aspect-[16/9]'
                      }`}
                    >
                      <img
                        src={product.image}
                        alt={product.name}
                        className={`w-full h-full transition-transform duration-300 ${
                          fitAllInView
                            ? 'object-contain bg-slate-950/70 p-2 group-hover:scale-[1.02]'
                            : 'object-cover group-hover:scale-[1.03]'
                        }`}
                        loading="lazy"
                      />
                    </div>
                    <div className="absolute inset-0 z-[1] bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent pointer-events-none" />
                    <ProductNewRibbon newUntil={product.newUntil} size="lg" className="z-[2]" />
                    {product.popular && (
                      <div
                        className={`absolute top-4 z-[3] ${isProductNew(product.newUntil) ? 'right-4' : 'left-4'}`}
                      >
                        <div className="bg-purple-600 text-white px-3 py-1 rounded-none text-xs font-medium">
                          Populär
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={`${cardPaddingClass} flex flex-col flex-grow`}>
                    <div className={`flex items-start justify-between gap-4 ${fitAllInView ? 'mb-2' : 'mb-4'}`}>
                      <div className="text-purple-200">
                        {React.createElement(getCategoryIcon(product.category), {
                          size: fitAllInView ? 18 : 22,
                          className: 'text-purple-200',
                        })}
                      </div>
                      <div className="text-right">
                        {product.priceOnRequest ? (
                          <>
                            <div className="text-lg font-bold text-purple-200">Enligt offert</div>
                            <div className="text-slate-400 text-xs">Begär offert</div>
                          </>
                        ) : (
                          <>
                            <PriceWithVatLabel
                              price={product.pricePerDay}
                              valueClassName="text-lg font-bold text-purple-200"
                              labelClassName="text-slate-400 text-xs"
                            />
                            <div className="text-slate-400 text-sm">/dag</div>
                          </>
                        )}
                      </div>
                    </div>

                    <h3 className={titleClass}>{product.name}</h3>
                    <p className={`text-slate-400 text-sm leading-relaxed flex-grow ${fitAllInView ? 'line-clamp-2' : 'line-clamp-3'}`}>
                      {product.shortDescription}
                    </p>

                    <div className={`${fitAllInView ? 'mt-3 pt-3' : 'mt-5 pt-5'} flex items-center justify-between gap-4 border-t border-slate-800`}>
                      {!fitAllInView && (
                        <div className="flex items-center gap-2 text-slate-200">
                          <div className="flex" aria-label="Betyg">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            ))}
                          </div>
                        </div>
                      )}

                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (cardCtaMode === 'homePopular') {
                            trackHomePopularPackageClick(product.id, homeCtaLabel);
                          }
                          const goesToBooking =
                            !product.priceOnRequest &&
                            (cardCtaMode === 'homePopular' || cardCtaMode === 'default') &&
                            primaryHref.includes('/booking/');
                          if (goesToBooking) {
                            trackBookingCtaClick(product, undefined, {
                              entry_point: 'carousel',
                              list_name: title,
                            });
                          }
                          window.location.href = primaryHref;
                        }}
                        className={`inline-flex items-center justify-center bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-none transition-colors ${
                          fitAllInView ? 'w-full py-2 px-3 text-sm' : 'py-3 px-5'
                        }`}
                      >
                        <span className="mr-2">{ctaText}</span>
                        <ArrowRight className="transition-transform group-hover:translate-x-[2px]" size={fitAllInView ? 16 : 18} />
                      </button>
                    </div>
                  </div>
                </Link>
              </div>
              );
            })}
          </div>
        </div>

        {ctaLink ? (
          <div className="text-center">
            <Link
              to={ctaLink}
              className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-none transition-colors"
            >
              <span className="mr-2">{ctaLabel}</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ProductCarousel;
